import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"
import { Calendar } from "fullcalendar"
import interactionPlugin from "fullcalendar/interaction"
import timeGridPlugin from "fullcalendar/timegrid"
import dayGridPlugin from "fullcalendar/daygrid"
import multiMonthPlugin from "fullcalendar/multimonth"
import themePlugin from "fullcalendar/themes/classic"

export default class extends Controller {
  static values = {
    weekStartDay: { type: Number, default: 1 },
    selectedWeekStart: String,
    venueTimeZone: String
  }

  static targets = [
    "calendar",
    "eventsData",
    "tenantsData",
    "areasData",
    "resourceFilter",
    "resourceFilterLabel",
    "fullDayToggle",
    "form",
    "methodInput",
    "entryKindInput",
    "tenantIdInput",
    "startsAtInput",
    "endsAtInput",
    "areaIdInput",
    "spaceIdInput",
    "sourceKindInput",
    "commentInput",
    "deleteForm",
    "deleteCommentInput"
  ]

  connect() {
    this.events = this.parseJson(this.eventsDataTarget)
    this.tenants = this.parseJson(this.tenantsDataTarget)
    this.areas = this.parseJson(this.areasDataTarget)

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [themePlugin, interactionPlugin, timeGridPlugin, dayGridPlugin, multiMonthPlugin],
      initialView: "timeGridWeek",
      initialDate: this.initialDate(),
      timeZone: this.venueTimeZoneValue || "local",
      firstDay: this.weekStartDayValue,
      height: "auto",
      selectable: true,
      allDaySlot: false,
      slotDuration: "00:15:00",
      snapDuration: "00:15:00",
      slotLabelInterval: "01:00:00",
      slotMinTime: "00:00:00",
      slotMaxTime: "24:00:00",
      dayHeaderContent: (arg) => this.dayHeaderContent(arg),
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "timeGridDay,timeGridWeek,dayGridMonth,multiMonthYear"
      },
      events: this.filteredEvents(),
      select: (selectionInfo) => this.openDialogForSelection(selectionInfo),
      eventClick: (clickInfo) => this.openDialogForEvent(clickInfo.event),
      datesSet: (dateInfo) => this.datesSet(dateInfo),
      views: {
        timeGridDay: { buttonText: "Day" },
        timeGridWeek: { buttonText: "Week" },
        dayGridMonth: { buttonText: "Month" },
        multiMonthYear: { buttonText: "Year" }
      }
    })

    this.calendar.render()
    this.updateResourceFilterLabel()
    this.applyVisibleWindow()
  }

  disconnect() {
    this.calendar?.destroy()
  }

  fullDayToggleChanged() {
    this.applyVisibleWindow()
  }

  resourceFilterChanged() {
    this.refreshEvents()
    this.updateResourceFilterLabel()
    this.applyVisibleWindow()
  }

  datesSet(dateInfo) {
    this.redirectToLoadedWeekIfNeeded(dateInfo)
    this.applyVisibleWindow()
  }

  async openDialogForSelection(selectionInfo) {
    await this.openDialog({
      recordId: null,
      entryKind: "tenant_allocation",
      tenantId: "",
      startAt: this.datetimeLocalValue(selectionInfo.start),
      endAt: this.datetimeLocalValue(selectionInfo.end),
      areaId: this.selectedResourceAreaId(),
      spaceId: this.selectedResourceSpaceId(),
      comment: "",
      sourceKind: "manual"
    }, false)
  }

  async openDialogForEvent(event) {
    const props = event.extendedProps
    if (props.grouped) {
      await Swal.fire({
        title: "Grouped booking",
        text: `${event.title} represents ${props.recordIds?.length || 0} venue resource bookings for the same tenant and time.`,
        confirmButtonText: "Close"
      })
      return
    }

    await this.openDialog({
      recordId: props.recordId,
      entryKind: props.entryKind,
      tenantId: String(props.tenantId || ""),
      startAt: this.datetimeLocalValue(event.start),
      endAt: this.datetimeLocalValue(event.end),
      areaId: String(props.bookableAreaId || ""),
      spaceId: String(props.bookableSpaceId || ""),
      comment: props.comment || "",
      sourceKind: props.sourceKind || "manual",
      affectedSessionCount: Number(props.affectedSessionCount || 0)
    }, true)
  }

  async openDialog(payload, editing) {
    const tenantOptions = this.optionHtml(this.tenants, payload.tenantId, "Select tenant…")
    const areaOptions = this.optionHtml(this.areas.map((area) => ({ id: area.id, name: area.name })), payload.areaId, "Venue-wide")
    const spaceOptions = this.spaceOptionHtml(payload.areaId, payload.spaceId)

    const result = await Swal.fire({
      title: editing ? "Edit booking" : "New booking",
      html: `
        <div class="grid gap-3 text-left">
          <label class="text-sm font-semibold text-stone-700">Booking type</label>
          <select id="swal-entry-kind" class="swal2-input booking-calendar-dialog-select !mt-0">
            <option value="no_access" ${payload.entryKind === "no_access" ? "selected" : ""}>Venue Closures</option>
            <option value="tenant_allocation" ${payload.entryKind === "tenant_allocation" ? "selected" : ""}>Tenant booking</option>
          </select>
          <div id="swal-tenant-wrapper">
            <label class="text-sm font-semibold text-stone-700">Tenant</label>
            <select id="swal-tenant" class="swal2-input booking-calendar-dialog-select !mt-0">${tenantOptions}</select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-sm font-semibold text-stone-700">Starts</label>
              <input id="swal-start-at" type="datetime-local" step="900" class="swal2-input !mt-0" value="${payload.startAt}">
            </div>
            <div>
              <label class="text-sm font-semibold text-stone-700">Ends</label>
              <input id="swal-end-at" type="datetime-local" step="900" class="swal2-input !mt-0" value="${payload.endAt}">
            </div>
          </div>
          <div id="swal-area-wrapper">
            <label class="text-sm font-semibold text-stone-700">Area</label>
            <select id="swal-area" class="swal2-input booking-calendar-dialog-select !mt-0">${areaOptions}</select>
          </div>
          <div id="swal-space-wrapper">
            <label class="text-sm font-semibold text-stone-700">Space</label>
            <select id="swal-space" class="swal2-input booking-calendar-dialog-select !mt-0">${spaceOptions}</select>
          </div>
          <label class="text-sm font-semibold text-stone-700">Comment</label>
          <textarea id="swal-comment" class="swal2-textarea !mt-0" placeholder="Optional note">${this.escapeHtml(payload.comment)}</textarea>
        </div>
      `,
      showCancelButton: true,
      showDenyButton: editing,
      confirmButtonText: editing ? "Save changes" : "Create booking",
      denyButtonText: "Delete",
      cancelButtonText: "Cancel",
      focusConfirm: false,
      width: 720,
      didOpen: () => {
        const entryKindSelect = document.getElementById("swal-entry-kind")
        const areaSelect = document.getElementById("swal-area")
        const tenantWrapper = document.getElementById("swal-tenant-wrapper")
        const areaWrapper = document.getElementById("swal-area-wrapper")
        const spaceWrapper = document.getElementById("swal-space-wrapper")
        const refreshVisibility = () => {
          const tenantBooking = entryKindSelect.value === "tenant_allocation"
          tenantWrapper.style.display = tenantBooking ? "" : "none"
          areaWrapper.style.display = tenantBooking ? "" : "none"
          spaceWrapper.style.display = tenantBooking ? "" : "none"
        }

        entryKindSelect.addEventListener("change", refreshVisibility)
        areaSelect.addEventListener("change", () => {
          const spaceSelect = document.getElementById("swal-space")
          spaceSelect.innerHTML = this.spaceOptionHtml(areaSelect.value, "")
        })
        refreshVisibility()
      },
      preConfirm: () => {
        const values = {
          entryKind: document.getElementById("swal-entry-kind").value,
          tenantId: document.getElementById("swal-tenant").value,
          startAt: document.getElementById("swal-start-at").value,
          endAt: document.getElementById("swal-end-at").value,
          areaId: document.getElementById("swal-area").value,
          spaceId: document.getElementById("swal-space").value,
          comment: document.getElementById("swal-comment").value
        }

        if (!values.startAt || !values.endAt) {
          Swal.showValidationMessage("Start and end are required.")
          return false
        }

        if (values.endAt <= values.startAt) {
          Swal.showValidationMessage("End time must be after the start time.")
          return false
        }

        if (values.entryKind === "tenant_allocation" && !values.tenantId) {
          Swal.showValidationMessage("Tenant bookings require a tenant.")
          return false
        }

        return values
      }
    })

    if (result.isDenied && editing) {
      if (!await this.confirmDelete(payload)) return

      this.deleteFormTarget.action = `${this.deleteFormTarget.dataset.urlBase}${payload.recordId}`
      this.deleteCommentInputTarget.value = payload.comment
      this.deleteFormTarget.requestSubmit()
      return
    }

    if (!result.isConfirmed) return

    const values = result.value
    this.formTarget.action = editing ? `${this.formTarget.dataset.updateUrlBase}${payload.recordId}` : this.formTarget.dataset.createUrl
    this.methodInputTarget.value = editing ? "patch" : "post"
    this.entryKindInputTarget.value = values.entryKind
    this.tenantIdInputTarget.value = values.entryKind === "tenant_allocation" ? values.tenantId : ""
    this.startsAtInputTarget.value = values.startAt
    this.endsAtInputTarget.value = values.endAt
    this.areaIdInputTarget.value = values.entryKind === "tenant_allocation" ? values.areaId : ""
    this.spaceIdInputTarget.value = values.entryKind === "tenant_allocation" ? values.spaceId : ""
    this.sourceKindInputTarget.value = "manual"
    this.commentInputTarget.value = values.comment
    this.formTarget.requestSubmit()
  }

  async confirmDelete(payload) {
    const affectedSessionCount = Number(payload.affectedSessionCount || 0)
    const linkedSessionText = affectedSessionCount > 0
      ? `This booking overlaps ${affectedSessionCount} active tenant session${affectedSessionCount === 1 ? "" : "s"}. The tenant's primary contact will be emailed and advised that they may want to cancel or adjust the linked session${affectedSessionCount === 1 ? "" : "s"}.`
      : "No active tenant sessions are currently shown as overlapping this booking."

    const result = await Swal.fire({
      title: "Delete venue booking?",
      text: `${linkedSessionText} This cannot be undone.`,
      icon: affectedSessionCount > 0 ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Delete booking",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#b45309",
      focusCancel: true
    })

    return result.isConfirmed
  }

  applyVisibleWindow() {
    if (!this.calendar.view.type.startsWith("timeGrid")) return

    if (this.fullDayToggleTarget.checked) {
      this.calendar.setOption("slotMinTime", "00:00:00")
      this.calendar.setOption("slotMaxTime", "24:00:00")
      return
    }

    const { startMinute, endMinute } = this.derivedVisibleWindow()
    this.calendar.setOption("slotMinTime", this.timeForMinute(startMinute))
    this.calendar.setOption("slotMaxTime", this.timeForMinute(endMinute))
  }

  refreshEvents() {
    this.calendar.removeAllEvents()
    this.calendar.addEventSource(this.filteredEvents())
  }

  filteredEvents() {
    return this.events.filter((event) => this.eventMatchesSelectedResource(event))
  }

  eventMatchesSelectedResource(event) {
    const selected = this.selectedResource()
    if (selected.type === "all") return true

    const props = event.extendedProps || {}
    const areaId = props.bookableAreaId ? String(props.bookableAreaId) : ""
    const spaceId = props.bookableSpaceId ? String(props.bookableSpaceId) : ""

    if (selected.type === "venue") return areaId === "" && spaceId === ""
    if (areaId === "" && spaceId === "") return true
    if (selected.type === "area") return areaId === selected.id
    if (selected.type === "space") return (areaId === selected.areaId && spaceId === "") || spaceId === selected.id

    return true
  }

  selectedResource() {
    const value = this.resourceFilterTarget.value || "all"
    if (value === "all") return { type: "all", label: "all resources" }
    if (value === "venue") return { type: "venue", label: "full venue" }

    const [type, id] = value.split(":", 2)
    if (type === "area") {
      const area = this.areas.find((record) => String(record.id) === id)
      return { type, id, label: area?.name || "selected area" }
    }

    if (type === "space") {
      const area = this.areas.find((record) => record.spaces.some((space) => String(space.id) === id))
      const space = area?.spaces.find((record) => String(record.id) === id)
      return { type, id, areaId: area ? String(area.id) : "", label: area && space ? `${area.name} / ${space.name}` : "selected space" }
    }

    return { type: "all", label: "all resources" }
  }

  selectedResourceAreaId() {
    const selected = this.selectedResource()
    if (selected.type === "area") return selected.id
    if (selected.type === "space") return selected.areaId

    return ""
  }

  selectedResourceSpaceId() {
    const selected = this.selectedResource()
    return selected.type === "space" ? selected.id : ""
  }

  updateResourceFilterLabel() {
    const selected = this.selectedResource()
    this.resourceFilterLabelTarget.textContent = `Showing ${selected.label}`
  }

  derivedVisibleWindow() {
    const rangeStart = this.calendar.view.currentStart
    const rangeEnd = this.calendar.view.currentEnd
    const dayKeys = []

    for (const cursor = new Date(rangeStart); cursor < rangeEnd; cursor.setDate(cursor.getDate() + 1)) {
      dayKeys.push(this.dayKey(cursor))
    }

    const leadingByDay = new Map()
    const trailingByDay = new Map()

    dayKeys.forEach((key) => {
      leadingByDay.set(key, 0)
      trailingByDay.set(key, 24 * 60)
    })

    this.events
      .filter((event) => event.extendedProps?.entryKind === "no_access")
      .forEach((event) => {
        const props = event.extendedProps
        const firstKey = props.venueStartDate
        const finalKey = props.venueEndMinute === 0 ? this.previousDateKey(props.venueEndDate) : props.venueEndDate
        if (!firstKey || !finalKey) return

        for (let key = firstKey; key <= finalKey; key = this.nextDateKey(key)) {
          if (!leadingByDay.has(key)) continue

          const overlapStartMinute = key === props.venueStartDate ? props.venueStartMinute : 0
          const overlapEndMinute = key === props.venueEndDate ? props.venueEndMinute : 24 * 60

          if (overlapStartMinute === 0) {
            leadingByDay.set(key, Math.max(leadingByDay.get(key), overlapEndMinute))
          }

          if (overlapEndMinute === 24 * 60) {
            trailingByDay.set(key, Math.min(trailingByDay.get(key), overlapStartMinute))
          }
        }
      })

    const startMinute = Math.max(...Array.from(leadingByDay.values()))
    const endMinute = Math.min(...Array.from(trailingByDay.values()))
    if (endMinute <= startMinute) return { startMinute: 0, endMinute: 24 * 60 }

    return { startMinute, endMinute }
  }

  optionHtml(records, selectedValue, blankLabel) {
    const blank = `<option value="">${blankLabel}</option>`
    return blank + records.map((record) => `<option value="${record.id}" ${String(record.id) === String(selectedValue) ? "selected" : ""}>${this.escapeHtml(record.name)}</option>`).join("")
  }

  spaceOptionHtml(areaId, selectedValue) {
    const blank = `<option value="">Whole venue or area</option>`
    const spaces = this.areas
      .filter((area) => areaId === "" || String(area.id) === String(areaId))
      .flatMap((area) => area.spaces.map((space) => ({ id: space.id, name: `${area.name} / ${space.name}` })))

    return blank + spaces.map((space) => `<option value="${space.id}" ${String(space.id) === String(selectedValue) ? "selected" : ""}>${this.escapeHtml(space.name)}</option>`).join("")
  }

  parseJson(element) {
    try {
      return JSON.parse(element.textContent || "[]")
    } catch (_error) {
      return []
    }
  }

  datetimeLocalValue(date) {
    const parts = this.datePartsInVenueTimeZone(date)
    const year = parts.year
    const month = parts.month
    const day = parts.day
    const hours = parts.hour
    const minutes = parts.minute
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  dayKey(date) {
    const parts = this.datePartsInVenueTimeZone(date)
    return `${parts.year}-${parts.month}-${parts.day}`
  }

  minuteOfDay(date) {
    const parts = this.datePartsInVenueTimeZone(date)
    return (Number(parts.hour) * 60) + Number(parts.minute)
  }

  timeForMinute(totalMinutes) {
    return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}:00`
  }

  initialDate() {
    if (this.hasSelectedWeekStartValue && this.selectedWeekStartValue) {
      return this.selectedWeekStartValue
    }

    return this.startOfCurrentWeek()
  }

  redirectToLoadedWeekIfNeeded(dateInfo) {
    if (!dateInfo.view.type.startsWith("timeGrid")) return

    const weekStart = this.weekStartForDate(dateInfo.start)
    const weekStartParam = this.dateParamInVenueTimeZone(weekStart)
    if (weekStartParam === this.selectedWeekStartValue) return

    const url = new URL(window.location.href)
    url.searchParams.set("week_start", weekStartParam)
    window.location.assign(url.toString())
  }

  weekStartForDate(date) {
    const parts = this.datePartsInVenueTimeZone(date)
    const clone = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00`)
    clone.setDate(clone.getDate() - ((Number(parts.weekday) - this.weekStartDayValue + 7) % 7))
    return clone
  }

  dateParam(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  }

  dateParamInVenueTimeZone(date) {
    const parts = this.datePartsInVenueTimeZone(date)
    return `${parts.year}-${parts.month}-${parts.day}`
  }

  nextDateKey(dateKey) {
    const date = new Date(`${dateKey}T00:00:00`)
    date.setDate(date.getDate() + 1)
    return this.dateParam(date)
  }

  previousDateKey(dateKey) {
    const date = new Date(`${dateKey}T00:00:00`)
    date.setDate(date.getDate() - 1)
    return this.dateParam(date)
  }

  datePartsInVenueTimeZone(date) {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: this.venueTimeZoneValue || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      hourCycle: "h23",
      weekday: "short"
    })
    const values = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]))
    const weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour === "24" ? "00" : values.hour,
      minute: values.minute,
      weekday: weekdays[values.weekday]
    }
  }

  mondayWeekday(date) {
    return (date.getDay() + 6) % 7
  }

  startOfCurrentWeek() {
    const today = new Date()
    const clone = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    clone.setDate(clone.getDate() - ((clone.getDay() - this.weekStartDayValue + 7) % 7))
    return clone
  }

  dayHeaderContent(arg) {
    if (arg.view.type !== "timeGridWeek") {
      return { text: arg.text }
    }

    const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(arg.date)
    const fullDate = new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(arg.date)

    return {
      html: `
        <div class="flex flex-col items-center">
          <span>${this.escapeHtml(weekday)}</span>
          <span class="mt-1 text-xs font-normal text-stone-500">${this.escapeHtml(fullDate)}</span>
        </div>
      `
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
